"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { z } from "zod";
import { recommendationQuestions, degreeRecommendationQuestions, modeQuestion } from "@/lib/mock-data";
import { DEGREE_OPTIONS, type DegreeOption } from "@/lib/degree-catalog";
import { getMacBlockers, hasMacBlockers, macBlockerMessage } from "@/lib/mac-compatibility";
import type { Answer } from "@/lib/match-explanations";

// The Q&A flow, lifted out of /recommend so the /spec pages can host it inline.
// It owns the questions, the widgets and the step state, and nothing else: what
// happens with the finished answers is the caller's business (see onComplete).

/* ─────────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────────── */

// degreeRecommendationQuestions is the software flow with the software question
// swapped for the degree picker, so its element type covers both flows.
type Question = (typeof degreeRecommendationQuestions)[number];
type FlowMode = "software" | "degree";

// Restoring saved progress has to land before the browser paints, or a refresh
// flashes question one before jumping back. Layout effects don't run on the
// server, so fall back to useEffect there to keep SSR quiet.
const useBrowserLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
type PersistedProgress = {
  mode?: FlowMode;
  step?: number;
  answers?: Record<string, Answer>;
};

/** Every brand we stock bar Apple — the fallback when Apple has been ruled out. */
const NON_APPLE_BRANDS: string[] = ((recommendationQuestions.find(q => q.id === "brand")?.options ?? []) as {
  value: string;
}[]).map(o => o.value).filter(v => v !== "apple");

/* ─────────────────────────────────────────────────────────────
   Currency helpers (driven by country answer)
───────────────────────────────────────────────────────────── */

const CURRENCY_MAP: Record<string, {
  symbol: string;
  code: string;
}> = {
  gb: {
    symbol: "£",
    code: "GBP"
  },
  us: {
    symbol: "$",
    code: "USD"
  }
};
function getCurrencyInfo(country?: string): {
  symbol: string;
  code: string;
} {
  return CURRENCY_MAP[country ?? ""] ?? {
    symbol: "£",
    code: "GBP"
  };
}

/* ─────────────────────────────────────────────────────────────
   Zod schema for budget validation
───────────────────────────────────────────────────────────── */

const budgetSchema = z.string().min(1, {
  message: "Please enter a budget."
}).refine(v => /^\d+(\.\d{1,2})?$/.test(v.trim()), {
  message: "Please enter a valid price (e.g. 950 or 999.99)."
}).refine(v => parseFloat(v) > 0, {
  message: "Budget must be greater than 0."
});

/* ─────────────────────────────────────────────────────────────
   hasAnswer — drives the Next button enabled state
───────────────────────────────────────────────────────────── */

function hasAnswer(type: string, v: Answer, id?: string): boolean {
  if (type === "multi-feature") return true;
  if (type === "degree") return typeof v === "string" && v !== "";
  if (type === "multi-squircle") return true; // brand selection is optional; empty = all brands
  if (type === "multi-icon") {
    if (id === "software") return Array.isArray(v) && v.length > 0;
    return Array.isArray(v); // games can be skipped
  }
  if (type === "currency") return budgetSchema.safeParse(v as string).success;
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && v !== "";
}

/* ─────────────────────────────────────────────────────────────
   RecommendationQuestionnaire
───────────────────────────────────────────────────────────── */

export type RecommendationQuestionnaireProps = {
  onComplete: (answers: Record<string, Answer>) => void | Promise<void>;
  initialAnswers?: Record<string, Answer>;
  /**
   * Pins the country instead of asking for it. A /spec/gb/... page must not let
   * the visitor answer "us", or their answers would contradict the URL the score
   * is being computed for.
   */
  lockedCountry?: string;
  /** Look the country up from the visitor's IP. Only /recommend wants this. */
  autoDetectCountry?: boolean;
  /**
   * sessionStorage key to keep the flow's progress under. Set it and a refresh
   * (or a trip out to another page and back) resumes on the same question with
   * the same answers instead of starting over. Left unset, nothing is stored.
   */
  persistKey?: string;
  submitLabel?: string;
  finalCtaLabel?: string;
};
export function RecommendationQuestionnaire({
  onComplete,
  initialAnswers,
  lockedCountry,
  autoDetectCountry = false,
  persistKey,
  submitLabel = "See my matches",
  finalCtaLabel = "Generate my recommendations →"
}: RecommendationQuestionnaireProps) {
  // The mode question comes first and decides the rest of the flow: "degree"
  // swaps the software question for a degree picker that pre-selects the
  // course's software. A locked country is already answered, so drop that
  // question from either flow.
  const questionsFor = (m: FlowMode) => {
    const base = m === "degree" ? degreeRecommendationQuestions : recommendationQuestions;
    return [modeQuestion, ...(lockedCountry ? base.filter(q => q.id !== "country") : base)];
  };
  const initialMode: FlowMode = initialAnswers?.["degree"] ? "degree" : "software";
  const [mode, setMode] = useState<FlowMode>(initialMode);
  const questions = questionsFor(mode);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Answer>>(() => ({
    // Only pre-answer the mode question when the caller already supplied a
    // software/degree answer to resume from; a fresh visitor picks it themselves.
    ...(initialAnswers?.["degree"] || initialAnswers?.["software"] ? {
      mode: initialMode
    } : {}),
    ...(lockedCountry ? {
      country: lockedCountry
    } : {}),
    ...(initialAnswers ?? {}),
    ...(lockedCountry ? {
      country: lockedCountry
    } : {})
  }));
  const currentQuestionRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const total = questions.length;
  const current = questions[step];
  const progress = (step + 1) / total * 100;

  // Saved progress is only read once, on mount; every render after that the
  // component's own state is the truth. Until that read has happened, writing
  // back would clobber the save with a fresh, empty flow.
  const [restored, setRestored] = useState(!persistKey);
  useBrowserLayoutEffect(() => {
    if (!persistKey) return;
    try {
      const raw = sessionStorage.getItem(persistKey);
      if (raw) {
        const saved = JSON.parse(raw) as PersistedProgress;
        const savedMode: FlowMode = saved.mode === "degree" ? "degree" : "software";
        setMode(savedMode);
        if (saved.answers && typeof saved.answers === "object") {
          setAnswers(prev => ({
            ...prev,
            ...saved.answers,
            // The URL still outranks anything saved from an earlier visit.
            ...(lockedCountry ? {
              country: lockedCountry
            } : {})
          }));
        }
        if (typeof saved.step === "number") {
          const savedTotal = questionsFor(savedMode).length;
          setStep(Math.max(0, Math.min(saved.step, savedTotal - 1)));
        }
      }
    } catch {
      /* unreadable or malformed save — just start the flow fresh */
    }
    setRestored(true);
  }, [persistKey]);
  useEffect(() => {
    if (!persistKey || !restored) return;
    try {
      sessionStorage.setItem(persistKey, JSON.stringify({
        mode,
        step,
        answers
      }));
    } catch {
      /* private mode / quota — persistence is a convenience, not a requirement */
    }
  }, [persistKey, restored, mode, step, answers]);

  // Auto-detect country from IP on first load
  useEffect(() => {
    if (!autoDetectCountry) return;
    fetch("https://ipapi.co/json/").then(res => res.json()).then(data => {
      const code = (data.country_code as string)?.toLowerCase();
      if (code === "gb" || code === "us") {
        setAnswers(prev_0 => prev_0["country"] ? prev_0 : {
          ...prev_0,
          country: code
        });
      }
    }).catch(() => {});
  }, [autoDetectCountry]);

  // Initialise multi-select answers to [] so the value is always an array
  useEffect(() => {
    if ((current.type === "multi-icon" || current.type === "multi-squircle") && answers[current.id] === undefined) {
      setAnswers(prev_1 => ({
        ...prev_1,
        [current.id]: []
      }));
    }
  }, [step, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll so the new Plample bubble sits at the top of the visible area — but
  // never past the questionnaire's own bottom edge. Near the end of the chat
  // there isn't a viewport's worth of content under the bubble, and a plain
  // scrollIntoView then runs to the bottom of the page, dragging whatever the
  // host renders below (the /compare footer) into view.
  useEffect(() => {
    const timer = setTimeout(() => {
      const question = currentQuestionRef.current;
      const root = rootRef.current;
      if (!question || !root) return;
      const questionTop = question.getBoundingClientRect().top + window.scrollY;
      const rootBottom = root.getBoundingClientRect().bottom + window.scrollY;
      const top = Math.max(0, Math.min(questionTop, rootBottom - window.innerHeight));
      window.scrollTo({
        top,
        behavior: "smooth"
      });
    }, 60);
    return () => clearTimeout(timer);
  }, [step]);
  const setAnswer = (v: Answer) => {
    if (current.id === "mode") {
      switchMode(v as FlowMode);
      return;
    }
    setAnswers(prev_2 => {
      // Picking a degree IS picking its software: mirror the degree's catalog
      // values into the software answer so the algorithm needs no changes.
      if (current.id === "degree") {
        const deg = DEGREE_OPTIONS.find(d => d.value === v);
        return {
          ...prev_2,
          degree: v,
          software: deg ? [...deg.softwareValues] : []
        };
      }
      return {
        ...prev_2,
        [current.id]: v
      };
    });
  };
  const switchMode = (m_0: FlowMode) => {
    setMode(m_0);
    // The flows only disagree on the software/degree step, so changing mode
    // discards whichever of the two was already answered.
    setAnswers(prev_3 => {
      const next: Record<string, Answer> = {
        ...prev_3,
        mode: m_0
      };
      delete next["software"];
      delete next["degree"];
      return next;
    });
    // Re-answering mode from an earlier step can't strand the user past the
    // swapped question, but clamp anyway so the flow stays consistent.
    const swapIdx = questionsFor(m_0).findIndex(q_0 => q_0.id === (m_0 === "degree" ? "degree" : "software"));
    setStep(prev_4 => Math.min(prev_4, swapIdx));
  };
  const next_0 = () => {
    if (step + 1 < total) setStep(step + 1);else finish();
  };
  const back = () => step > 0 && setStep(step - 1);

  // Demanding games and Windows-only engineering software both rule an Apple
  // laptop out. Those questions come before the OS and brand ones, but the
  // visitor can always go back and add a pick afterwards, so re-derive this on
  // every render rather than trusting the question order.
  const macBlockers = getMacBlockers(answers);
  const appleRuledOut = hasMacBlockers(macBlockers);

  // The option to grey out on whichever of the two questions is on screen.
  const blockedValues = !appleRuledOut ? [] : current.id === "brand" ? ["apple"] : current.id === "os" ? ["mac"] : [];

  // Rather than rewrite state behind the visitor's back, drop the Apple answers
  // on the way out. An empty brand answer means "consider every brand" to the
  // algorithm, Apple included, so name the others explicitly when Apple is out.
  const withoutApple = (a: Record<string, Answer>): Record<string, Answer> => {
    if (!appleRuledOut) return a;
    const next_1 = {
      ...a
    };
    const brands = (a["brand"] as string[] | undefined ?? []).filter(b => b !== "apple");
    next_1["brand"] = brands.length > 0 ? brands : NON_APPLE_BRANDS;
    if (next_1["os"] === "mac") delete next_1["os"];
    return next_1;
  };
  const finish = () => void onComplete(withoutApple(answers));

  // A macOS answer given before the blocking pick shows as unselected, so make
  // the visitor answer the OS question again rather than let a struck-through
  // option carry them forward.
  const canProceed = hasAnswer(current.type, answers[current.id], current.id) && !blockedValues.includes(answers[current.id] as string);
  return <div ref={rootRef} className="flex min-h-screen flex-col bg-background">
      {/* Progress bar */}
      <div className="border-b border-border/60 bg-background">
        <div className="mx-auto max-w-3xl px-5 py-3 sm:px-8">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              Question <span className="font-semibold text-foreground">{step + 1}</span> of {total}
            </span>
          </div>
          <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface">
            <div className="h-full rounded-full bg-brand transition-[width] duration-500" style={{
            width: `${progress}%`
          }} />
          </div>
        </div>
      </div>

      {/* Scrollable chat area */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-16">
          <ChatHistory questions={questions} answers={withoutApple(answers)} upTo={step} />

          <div key={current.id} ref={currentQuestionRef} className="plample-rise">
            <AssistantBubble>
              <p className="text-base leading-relaxed text-foreground sm:text-lg">{current.prompt}</p>
              <p className="mt-2 text-sm italic text-muted-foreground">{current.reassurance}</p>
            </AssistantBubble>

            {blockedValues.length > 0 && <div className="mt-4 ml-11 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4">
                <p className="text-sm leading-relaxed text-foreground">{macBlockerMessage(macBlockers)}</p>
              </div>}

            <div className="mt-5 pl-11">
              <AnswerWidget question={current} value={answers[current.id]} onChange={setAnswer} onSubmit={next_0} answers={answers} blockedValues={blockedValues} />
            </div>
          </div>

          {/* Final CTA */}
          {step === total - 1 && canProceed && <div className="mt-10 rounded-2xl border border-dashed border-brand/40 bg-brand/5 p-6 text-center plample-rise">
              <p className="text-sm text-foreground">
                That&apos;s everything we need. Ready to see how well this fits you?
              </p>
              <button onClick={finish} className="mt-4 inline-flex h-11 cursor-pointer items-center justify-center rounded-full bg-brand px-6 text-sm font-semibold text-brand-foreground hover:opacity-95">
                {finalCtaLabel}
              </button>
            </div>}
        </div>
      </div>

      {/* Sticky footer nav */}
      <div className="sticky bottom-0 border-t border-border/60 bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-5 py-3 sm:px-8">
          <button onClick={back} disabled={step === 0} className="inline-flex h-10 cursor-pointer items-center rounded-full px-4 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30">
            ← Back
          </button>
          <button onClick={next_0} disabled={!canProceed} className="inline-flex h-10 cursor-pointer items-center rounded-full bg-foreground px-5 text-sm font-semibold text-background transition-all hover:opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30">
            {step + 1 === total ? submitLabel : "Next"}
          </button>
        </div>
      </div>
    </div>;
}
/* ─────────────────────────────────────────────────────────────
   Chat history
───────────────────────────────────────────────────────────── */

function ChatHistory(t0) {
  const $ = _c(8);
  const {
    questions,
    answers,
    upTo
  } = t0;
  let t1;
  if ($[0] !== answers || $[1] !== questions || $[2] !== upTo) {
    let t2;
    if ($[4] !== answers) {
      t2 = q => <div key={q.id} className="space-y-3"><AssistantBubble compact={true}>{q.prompt}</AssistantBubble><UserBubble>{formatAnswer(q, answers[q.id], answers)}</UserBubble></div>;
      $[4] = answers;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = questions.slice(0, upTo).map(t2);
    $[0] = answers;
    $[1] = questions;
    $[2] = upTo;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <div className="space-y-6">{t1}</div>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function formatAnswer(q: Question, v: Answer, allAnswers: Record<string, Answer>): string {
  if (v == null || v === "") return "—";
  if (q.type === "currency") {
    const {
      symbol
    } = getCurrencyInfo(allAnswers["country"] as string | undefined);
    return `${symbol}${v}`;
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return q.id === "brand" ? "All brands" : "None selected";
    const labels = v.map(val => {
      const opt = (q as {
        options?: {
          value: string;
          label: string;
        }[];
      }).options?.find(o => o.value === val);
      return opt?.label ?? val;
    });
    return labels.join(", ");
  }
  const opt = (q as {
    options?: {
      value: string;
      label: string;
    }[];
  }).options?.find(o => o.value === v);
  return opt?.label ?? String(v);
}

/* ─────────────────────────────────────────────────────────────
   Bubble components
───────────────────────────────────────────────────────────── */

function AssistantBubble(t0) {
  const $ = _c(4);
  const {
    children,
    compact
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="grid size-8 shrink-0 place-items-center rounded-full bg-foreground text-[10px] font-bold text-background">P</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `max-w-[85%] rounded-2xl rounded-tl-md bg-card px-4 ring-1 ring-border/60 ${compact ? "py-2 text-sm text-muted-foreground" : "py-3"}`;
  let t3;
  if ($[1] !== children || $[2] !== t2) {
    t3 = <div className="flex items-start gap-3">{t1}<div className={t2}>{children}</div></div>;
    $[1] = children;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function UserBubble(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex items-start justify-end"><div className="max-w-[85%] rounded-2xl rounded-tr-md bg-foreground px-4 py-2 text-sm text-background">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/* ─────────────────────────────────────────────────────────────
   AnswerWidget dispatcher
───────────────────────────────────────────────────────────── */

function AnswerWidget(t0) {
  const $ = _c(41);
  const {
    question,
    value,
    onChange,
    onSubmit,
    answers,
    blockedValues: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const blockedValues = t2;
  switch (question.type) {
    case "currency":
      {
        const t3 = value as string | undefined;
        const t4 = answers.country as string | undefined;
        let t5;
        if ($[2] !== onChange || $[3] !== onSubmit || $[4] !== t3 || $[5] !== t4) {
          t5 = <CurrencyInput value={t3} onChange={onChange} onSubmit={onSubmit} countryCode={t4} />;
          $[2] = onChange;
          $[3] = onSubmit;
          $[4] = t3;
          $[5] = t4;
          $[6] = t5;
        } else {
          t5 = $[6];
        }
        return t5;
      }
    case "country":
      {
        const t3 = value as string | undefined;
        let t4;
        if ($[7] !== onChange || $[8] !== question.options || $[9] !== t3) {
          t4 = <CountrySelect options={question.options} value={t3} onChange={onChange} />;
          $[7] = onChange;
          $[8] = question.options;
          $[9] = t3;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        return t4;
      }
    case "single":
      {
        const t3 = value as string | undefined;
        let t4;
        if ($[11] !== blockedValues || $[12] !== onChange || $[13] !== question.options || $[14] !== t3) {
          t4 = <SingleSelect options={question.options} value={t3} onChange={onChange} blockedValues={blockedValues} />;
          $[11] = blockedValues;
          $[12] = onChange;
          $[13] = question.options;
          $[14] = t3;
          $[15] = t4;
        } else {
          t4 = $[15];
        }
        return t4;
      }
    case "degree":
      {
        const t3 = question.options as DegreeOption[];
        const t4 = value as string | undefined;
        let t5;
        if ($[16] !== onChange || $[17] !== t3 || $[18] !== t4) {
          t5 = <DegreeSelect options={t3} value={t4} onChange={onChange} />;
          $[16] = onChange;
          $[17] = t3;
          $[18] = t4;
          $[19] = t5;
        } else {
          t5 = $[19];
        }
        return t5;
      }
    case "multi-icon":
      {
        const t3 = question.options as {
          value: string;
          label: string;
          image: string;
          category?: string;
        }[];
        let t4;
        if ($[20] !== value) {
          t4 = value as string[] ?? [];
          $[20] = value;
          $[21] = t4;
        } else {
          t4 = $[21];
        }
        let t5;
        if ($[22] !== onChange || $[23] !== t3 || $[24] !== t4) {
          t5 = <SearchableMultiSelect options={t3} value={t4} onChange={onChange} />;
          $[22] = onChange;
          $[23] = t3;
          $[24] = t4;
          $[25] = t5;
        } else {
          t5 = $[25];
        }
        return t5;
      }
    case "squircle":
      {
        const t3 = question.options as {
          value: string;
          label: string;
          sub?: string;
        }[];
        const t4 = value as string | undefined;
        let t5;
        if ($[26] !== onChange || $[27] !== t3 || $[28] !== t4) {
          t5 = <SquircleGrid options={t3} value={t4} onChange={onChange} />;
          $[26] = onChange;
          $[27] = t3;
          $[28] = t4;
          $[29] = t5;
        } else {
          t5 = $[29];
        }
        return t5;
      }
    case "multi-squircle":
      {
        const t3 = question.options as {
          value: string;
          label: string;
          sub?: string;
        }[];
        let t4;
        if ($[30] !== value) {
          t4 = value as string[] ?? [];
          $[30] = value;
          $[31] = t4;
        } else {
          t4 = $[31];
        }
        let t5;
        if ($[32] !== blockedValues || $[33] !== onChange || $[34] !== t3 || $[35] !== t4) {
          t5 = <MultiSquircleGrid options={t3} value={t4} onChange={onChange} blockedValues={blockedValues} />;
          $[32] = blockedValues;
          $[33] = onChange;
          $[34] = t3;
          $[35] = t4;
          $[36] = t5;
        } else {
          t5 = $[36];
        }
        return t5;
      }
    case "multi-feature":
      {
        const t3 = question.options as {
          value: string;
          label: string;
          defaultChecked?: boolean;
        }[];
        const t4 = value as string[] | undefined;
        let t5;
        if ($[37] !== onChange || $[38] !== t3 || $[39] !== t4) {
          t5 = <MultiFeature options={t3} value={t4} onChange={onChange} />;
          $[37] = onChange;
          $[38] = t3;
          $[39] = t4;
          $[40] = t5;
        } else {
          t5 = $[40];
        }
        return t5;
      }
    default:
      {
        return null;
      }
  }
}

/* ─────────────────────────────────────────────────────────────
   CurrencyInput — Zod validation + dynamic currency symbol
───────────────────────────────────────────────────────────── */

function CurrencyInput(t0) {
  const $ = _c(30);
  const {
    value,
    onChange,
    onSubmit,
    countryCode
  } = t0;
  let t1;
  if ($[0] !== countryCode) {
    t1 = getCurrencyInfo(countryCode);
    $[0] = countryCode;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    symbol,
    code
  } = t1;
  const [error, setError] = useState(null);
  const [, setTouched] = useState(false);
  const validate = _temp5;
  let t2;
  if ($[2] !== onChange) {
    t2 = v_0 => {
      onChange(v_0);
      if (v_0) {
        setError(validate(v_0));
      } else {
        setError(null);
      }
    };
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleChange = t2;
  let t3;
  if ($[4] !== value) {
    t3 = () => {
      setTouched(true);
      setError(validate(value ?? ""));
    };
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleBlur = t3;
  let t4;
  if ($[6] !== onSubmit || $[7] !== value) {
    t4 = e => {
      e.preventDefault();
      setTouched(true);
      const err = validate(value ?? "");
      if (err) {
        setError(err);
        return;
      }
      onSubmit();
    };
    $[6] = onSubmit;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSubmit = t4;
  const t5 = `flex items-center gap-3 rounded-2xl bg-card p-4 ring-1 transition-shadow focus-within:ring-2 ${error ? "ring-destructive focus-within:ring-destructive" : "ring-border/60 focus-within:ring-brand"}`;
  let t6;
  if ($[9] !== symbol) {
    t6 = <span className="text-xl font-semibold text-muted-foreground">{symbol}</span>;
    $[9] = symbol;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = value ?? "";
  let t8;
  if ($[11] !== handleChange) {
    t8 = e_0 => handleChange(e_0.target.value);
    $[11] = handleChange;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== handleBlur || $[14] !== t7 || $[15] !== t8) {
    t9 = <input autoFocus={true} inputMode="decimal" placeholder="950" value={t7} onChange={t8} onBlur={handleBlur} className="w-full bg-transparent text-2xl font-semibold tracking-tight outline-none placeholder:text-muted-foreground/40" />;
    $[13] = handleBlur;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== code) {
    t10 = <span className="rounded-full bg-surface px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{code}</span>;
    $[17] = code;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t5 || $[21] !== t6 || $[22] !== t9) {
    t11 = <div className={t5}>{t6}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t5;
    $[21] = t6;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== error) {
    t12 = error && <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive"><svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5 shrink-0"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1ZM8 5a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 8 5Zm0 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" /></svg>{error}</p>;
    $[24] = error;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== handleSubmit || $[27] !== t11 || $[28] !== t12) {
    t13 = <form onSubmit={handleSubmit}>{t11}{t12}</form>;
    $[26] = handleSubmit;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}

/* ─────────────────────────────────────────────────────────────
   CountrySelect
───────────────────────────────────────────────────────────── */
function _temp5(v) {
  const result = budgetSchema.safeParse(v);
  return result.success ? null : result.error.errors[0]?.message ?? "Invalid value.";
}
function CountrySelect(t0) {
  const $ = _c(12);
  const {
    options,
    value,
    onChange
  } = t0;
  const t1 = value ?? "";
  let t2;
  if ($[0] !== onChange) {
    t2 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <option value="" disabled={true}>Choose your country</option>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== options) {
    t4 = options.map(_temp6);
    $[3] = options;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t4) {
    t5 = <select value={t1} onChange={t2} className="w-full cursor-pointer appearance-none rounded-2xl bg-card px-4 py-4 pr-10 text-base font-medium ring-1 ring-border/60 focus:outline-none focus:ring-2 focus:ring-brand">{t3}{t4}</select>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"><path fillRule="evenodd" d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" /></svg>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div className="relative">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}

/* ─────────────────────────────────────────────────────────────
   SingleSelect
───────────────────────────────────────────────────────────── */
function _temp6(o) {
  return <option key={o.value} value={o.value}>{o.label}</option>;
}
function SingleSelect(t0) {
  const $ = _c(13);
  const {
    options,
    value,
    onChange,
    blockedValues: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const blockedValues = t2;
  const hasSub = options.some(_temp7);
  hasSub ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-3";
  let t3;
  if ($[2] !== blockedValues || $[3] !== onChange || $[4] !== options || $[5] !== value) {
    let t4;
    if ($[7] !== blockedValues || $[8] !== onChange || $[9] !== value) {
      t4 = o_0 => {
        const blocked = blockedValues.includes(o_0.value);
        const active = !blocked && value === o_0.value;
        return <button key={o_0.value} onClick={() => onChange(o_0.value)} disabled={blocked} title={blocked ? "Ruled out by your software or game picks" : undefined} className={`rounded-2xl px-4 py-4 text-left text-sm ring-1 transition-all ${blocked ? "cursor-not-allowed bg-card text-muted-foreground opacity-45 ring-border/60 line-through" : active ? "cursor-pointer bg-foreground text-background ring-foreground" : "cursor-pointer bg-card text-foreground ring-border/60 hover:ring-foreground/40"}`}><span className="block font-semibold">{o_0.label}</span>{o_0.sub && <span className={`mt-1 block text-xs leading-snug ${active && !blocked ? "text-background/65" : "text-muted-foreground"}`}>{o_0.sub}</span>}</button>;
      };
      $[7] = blockedValues;
      $[8] = onChange;
      $[9] = value;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = options.map(t4);
    $[2] = blockedValues;
    $[3] = onChange;
    $[4] = options;
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <div className="grid gap-2 grid-cols-1 sm:grid-cols-3">{t3}</div>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}

/* ─────────────────────────────────────────────────────────────
   OptionThumb — renders an app/game logo, with a letter fallback
   when the remote image is missing or fails to load.
───────────────────────────────────────────────────────────── */
function _temp7(o) {
  return o.sub;
}
function OptionThumb(t0) {
  const $ = _c(7);
  const {
    src,
    label,
    className
  } = t0;
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    const t1 = `shrink-0 rounded bg-surface ${className ?? ""}`;
    let t2;
    if ($[0] !== t1) {
      t2 = <span aria-hidden={true} className={t1} />;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setFailed(true);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = `shrink-0 rounded object-contain ${className ?? ""}`;
  let t3;
  if ($[3] !== label || $[4] !== src || $[5] !== t2) {
    t3 = <img src={src} alt={label} onError={t1} className={t2} />;
    $[3] = label;
    $[4] = src;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/* ─────────────────────────────────────────────────────────────
   SearchableMultiSelect — replaces MultiIcon for software & games
───────────────────────────────────────────────────────────── */

function SearchableMultiSelect(t0) {
  const $ = _c(38);
  const {
    options,
    value,
    onChange
  } = t0;
  const [query, setQuery] = useState("");
  const [fullPageOpen, setFullPageOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [fullPagePending, setFullPagePending] = useState(t1);
  const [fullPageQuery, setFullPageQuery] = useState("");
  let t2;
  if ($[1] !== fullPageOpen || $[2] !== fullPagePending || $[3] !== fullPageQuery || $[4] !== onChange || $[5] !== options || $[6] !== query || $[7] !== value) {
    const filtered = query ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase())) : [];
    const filteredFullPage = fullPageQuery ? options.filter(o_0 => o_0.label.toLowerCase().includes(fullPageQuery.toLowerCase())) : options;
    const groups = new Map();
    for (const o_1 of filteredFullPage) {
      const key = o_1.category ?? "";
      const bucket = groups.get(key);
      if (bucket) {
        bucket.push(o_1);
      } else {
        groups.set(key, [o_1]);
      }
    }
    const groupedFullPage = [...groups.entries()];
    let t3;
    if ($[9] !== onChange || $[10] !== value) {
      t3 = v => {
        const next = value.includes(v) ? value.filter(x => x !== v) : [...value, v];
        onChange(next);
        setQuery("");
      };
      $[9] = onChange;
      $[10] = value;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    const toggle = t3;
    let t4;
    if ($[12] !== onChange || $[13] !== value) {
      t4 = v_0 => onChange(value.filter(x_0 => x_0 !== v_0));
      $[12] = onChange;
      $[13] = value;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    const remove = t4;
    let t5;
    if ($[15] !== value) {
      t5 = () => {
        setFullPagePending([...value]);
        setFullPageQuery("");
        setFullPageOpen(true);
      };
      $[15] = value;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    const openFullPage = t5;
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = v_1 => setFullPagePending(prev => prev.includes(v_1) ? prev.filter(x_1 => x_1 !== v_1) : [...prev, v_1]);
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    const toggleFullPageItem = t6;
    let t7;
    if ($[18] !== fullPagePending || $[19] !== onChange) {
      t7 = () => {
        onChange(fullPagePending);
        setFullPageOpen(false);
        setQuery("");
      };
      $[18] = fullPagePending;
      $[19] = onChange;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    const confirmFullPage = t7;
    let t8;
    if ($[21] !== options || $[22] !== remove || $[23] !== value) {
      t8 = value.length > 0 && <div className="flex flex-wrap gap-2">{value.map(v_2 => {
          const opt = options.find(o_2 => o_2.value === v_2);
          if (!opt) {
            return null;
          }
          return <span key={v_2} className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand ring-1 ring-brand/30"><OptionThumb src={opt.image} label={opt.label} className="size-4" />{opt.label}<button type="button" onClick={() => remove(v_2)} aria-label={`Remove ${opt.label}`} className="ml-0.5 grid size-4 place-items-center rounded-full opacity-60 hover:opacity-100 transition-opacity"><svg viewBox="0 0 16 16" fill="currentColor" className="size-2.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg></button></span>;
        })}</div>;
      $[21] = options;
      $[22] = remove;
      $[23] = value;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    let t9;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"><path d="M6 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM0 6a6 6 0 1 1 10.89 3.477l4.817 4.816a.75.75 0 0 1-1.06 1.06l-4.816-4.816A6 6 0 0 1 0 6Z" /></svg>;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = e => setQuery(e.target.value);
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    let t12;
    if ($[27] !== query) {
      t11 = <input type="text" placeholder={"Type the name of what you're looking for\u2026"} value={query} onChange={t10} className="w-full rounded-2xl bg-card py-3 pl-9 pr-9 text-sm ring-1 ring-border/60 focus:outline-none focus:ring-2 focus:ring-brand" />;
      t12 = query && <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Clear search"><svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg></button>;
      $[27] = query;
      $[28] = t11;
      $[29] = t12;
    } else {
      t11 = $[28];
      t12 = $[29];
    }
    let t13;
    if ($[30] !== t11 || $[31] !== t12) {
      t13 = <div className="relative flex-1">{t9}{t11}{t12}</div>;
      $[30] = t11;
      $[31] = t12;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== openFullPage) {
      t14 = <button type="button" onClick={openFullPage} className="whitespace-nowrap cursor-pointer rounded-2xl bg-card px-4 py-3 text-sm font-medium ring-1 ring-border/60 transition-colors hover:bg-surface">Browse all</button>;
      $[33] = openFullPage;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    let t15;
    if ($[35] !== t13 || $[36] !== t14) {
      t15 = <div className="flex gap-2">{t13}{t14}</div>;
      $[35] = t13;
      $[36] = t14;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    t2 = <div className="space-y-3">{t8}{t15}{query && <div className="overflow-hidden rounded-2xl bg-card ring-1 ring-border/60">{filtered.length === 0 ? <p className="px-4 py-3 text-sm text-muted-foreground">No results for “{query}”</p> : <ul className="divide-y divide-border/60">{filtered.map(o_3 => {
            const active = value.includes(o_3.value);
            return <li key={o_3.value}><button type="button" onClick={() => toggle(o_3.value)} className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-surface"><OptionThumb src={o_3.image} label={o_3.label} className="size-6" /><span className="flex-1 font-medium">{o_3.label}</span>{active && <svg viewBox="0 0 16 16" fill="currentColor" className="size-4 shrink-0 text-brand"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>}</button></li>;
          })}</ul>}</div>}{fullPageOpen && createPortal(<div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex flex-col bg-background"><div className="flex items-center gap-3 border-b border-border/60 px-4 py-4 sm:px-6"><button type="button" onClick={() => setFullPageOpen(false)} aria-label="Go back" className="grid size-9 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background"><svg viewBox="0 0 16 16" fill="currentColor" className="size-4"><path fillRule="evenodd" d="M6.78 4.22a.75.75 0 0 1 0 1.06L4.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L2.47 8.53a.75.75 0 0 1 0-1.06l3.25-3.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg></button><div className="flex-1 min-w-0"><h3 className="text-base font-semibold">Browse all options</h3>{fullPagePending.length > 0 && <p className="text-xs text-muted-foreground">{fullPagePending.length} selected</p>}</div></div><div className="border-b border-border/60 px-4 py-3 sm:px-6"><div className="relative"><svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"><path d="M6 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM0 6a6 6 0 1 1 10.89 3.477l4.817 4.816a.75.75 0 0 1-1.06 1.06l-4.816-4.816A6 6 0 0 1 0 6Z" /></svg><input type="text" placeholder={"Type the name of what you're looking for\u2026"} value={fullPageQuery} onChange={e_0 => setFullPageQuery(e_0.target.value)} autoFocus={true} className="w-full rounded-xl bg-surface py-2.5 pl-9 pr-9 text-sm ring-1 ring-border/60 focus:outline-none focus:ring-2 focus:ring-brand" />{fullPageQuery && <button type="button" onClick={() => setFullPageQuery("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"><svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg></button>}</div></div>{fullPagePending.length > 0 && <div className="border-b border-border/60 px-4 py-3 sm:px-6"><p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-brand">Selected</p><div className="flex flex-wrap gap-2">{fullPagePending.map(v_3 => {
              const opt_0 = options.find(o_4 => o_4.value === v_3);
              if (!opt_0) {
                return null;
              }
              return <span key={v_3} className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-medium text-brand ring-1 ring-brand/30"><OptionThumb src={opt_0.image} label={opt_0.label} className="size-4" /> {opt_0.label}<button type="button" onClick={() => toggleFullPageItem(v_3)} aria-label={`Remove ${opt_0.label}`} className="ml-0.5 grid size-3.5 place-items-center rounded-full opacity-60 hover:opacity-100 transition-opacity"><svg viewBox="0 0 16 16" fill="currentColor" className="size-2.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg></button></span>;
            })}</div></div>}<div className="flex-1 overflow-y-auto">{filteredFullPage.length === 0 ? <p className="px-6 py-12 text-center text-sm text-muted-foreground">No results for “{fullPageQuery}”</p> : <div className="space-y-6 p-4 sm:p-6">{groupedFullPage.map(t16 => {
              const [category, items] = t16;
              return <section key={category || "_uncategorised"}>{category && <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{category}</h3>}<div className="flex flex-wrap gap-3">{items.map(o_5 => {
                    const active_0 = fullPagePending.includes(o_5.value);
                    return <div key={o_5.value} className="flex flex-col items-center gap-1"><button type="button" onClick={() => toggleFullPageItem(o_5.value)} className={`grid size-20 cursor-pointer place-items-center rounded-2xl ring-1 transition-all ${active_0 ? "bg-card shadow-md ring-2 ring-brand ring-offset-2 ring-offset-background" : "bg-card ring-border/60 hover:ring-foreground/40"}`}><OptionThumb src={o_5.image} label={o_5.label} className="size-9" /></button><span className="w-24 text-center text-base font-medium leading-tight text-muted-foreground break-words">{o_5.label}</span></div>;
                  })}</div></section>;
            })}</div>}</div><div className="border-t border-border/60 px-4 py-4 sm:px-6"><button type="button" onClick={confirmFullPage} className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-foreground text-sm font-semibold text-background transition-all hover:opacity-90 active:scale-95">Confirm{fullPagePending.length > 0 && <span className="rounded-full bg-background/20 px-2.5 py-0.5 text-xs">{fullPagePending.length} selected</span>}</button></div></div>, document.body)}</div>;
    $[1] = fullPageOpen;
    $[2] = fullPagePending;
    $[3] = fullPageQuery;
    $[4] = onChange;
    $[5] = options;
    $[6] = query;
    $[7] = value;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/* ─────────────────────────────────────────────────────────────
   DegreeSelect — single-select degree picker for the degree-based
   flow. Same search-bar + browse-all UX as software/games; picking
   a degree shows its profile (summary + software table).
───────────────────────────────────────────────────────────── */

function DegreeSelect(t0) {
  const $ = _c(48);
  const {
    options,
    value,
    onChange
  } = t0;
  const [query, setQuery] = useState("");
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseQuery, setBrowseQuery] = useState("");
  const matches = _temp8;
  let browseFiltered;
  let pick;
  let selected;
  let t1;
  let t2;
  let t3;
  if ($[0] !== browseQuery || $[1] !== onChange || $[2] !== options || $[3] !== query || $[4] !== value) {
    const filtered = query ? options.filter(o_0 => matches(o_0, query)) : [];
    let t4;
    if ($[11] !== browseQuery || $[12] !== options) {
      t4 = browseQuery ? options.filter(o_1 => matches(o_1, browseQuery)) : options;
      $[11] = browseQuery;
      $[12] = options;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    browseFiltered = t4;
    let t5;
    if ($[14] !== options || $[15] !== value) {
      let t6;
      if ($[17] !== value) {
        t6 = o_2 => o_2.value === value;
        $[17] = value;
        $[18] = t6;
      } else {
        t6 = $[18];
      }
      t5 = options.find(t6);
      $[14] = options;
      $[15] = value;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    selected = t5;
    let t6;
    if ($[19] !== onChange) {
      t6 = v => {
        onChange(v);
        setQuery("");
        setBrowseOpen(false);
      };
      $[19] = onChange;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    pick = t6;
    let t7;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => {
        setBrowseQuery("");
        setBrowseOpen(true);
      };
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    const openBrowse = t7;
    t1 = "space-y-3";
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"><path d="M6 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM0 6a6 6 0 1 1 10.89 3.477l4.817 4.816a.75.75 0 0 1-1.06 1.06l-4.816-4.816A6 6 0 0 1 0 6Z" /></svg>;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = e => setQuery(e.target.value);
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    let t11;
    if ($[24] !== query) {
      t10 = <input type="text" placeholder={"Type your degree, e.g. Computer Science\u2026"} value={query} onChange={t9} className="w-full rounded-2xl bg-card py-3 pl-9 pr-9 text-sm ring-1 ring-border/60 focus:outline-none focus:ring-2 focus:ring-brand" />;
      t11 = query && <button type="button" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label="Clear search"><svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg></button>;
      $[24] = query;
      $[25] = t10;
      $[26] = t11;
    } else {
      t10 = $[25];
      t11 = $[26];
    }
    let t12;
    if ($[27] !== t10 || $[28] !== t11) {
      t12 = <div className="relative flex-1">{t8}{t10}{t11}</div>;
      $[27] = t10;
      $[28] = t11;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <button type="button" onClick={openBrowse} className="whitespace-nowrap cursor-pointer rounded-2xl bg-card px-4 py-3 text-sm font-medium ring-1 ring-border/60 transition-colors hover:bg-surface">Browse all</button>;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    if ($[31] !== t12) {
      t2 = <div className="flex gap-2">{t12}{t13}</div>;
      $[31] = t12;
      $[32] = t2;
    } else {
      t2 = $[32];
    }
    t3 = query && <div className="overflow-hidden rounded-2xl bg-card ring-1 ring-border/60">{filtered.length === 0 ? <p className="px-4 py-3 text-sm text-muted-foreground">No results for “{query}”</p> : <ul className="divide-y divide-border/60">{filtered.map(o_3 => <li key={o_3.value}><button type="button" onClick={() => pick(o_3.value)} className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-surface"><span className="min-w-0 flex-1"><span className="block font-medium">{o_3.label}</span><span className="block truncate text-xs text-muted-foreground">{o_3.degrees}</span></span>{o_3.value === value && <svg viewBox="0 0 16 16" fill="currentColor" className="size-4 shrink-0 text-brand"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>}</button></li>)}</ul>}</div>;
    $[0] = browseQuery;
    $[1] = onChange;
    $[2] = options;
    $[3] = query;
    $[4] = value;
    $[5] = browseFiltered;
    $[6] = pick;
    $[7] = selected;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    browseFiltered = $[5];
    pick = $[6];
    selected = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  let t4;
  if ($[33] !== onChange || $[34] !== selected) {
    t4 = selected && <DegreeProfile degree={selected} onClear={() => onChange(null)} />;
    $[33] = onChange;
    $[34] = selected;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  let t5;
  if ($[36] !== browseFiltered || $[37] !== browseOpen || $[38] !== browseQuery || $[39] !== pick || $[40] !== value) {
    t5 = browseOpen && createPortal(<div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex flex-col bg-background"><div className="flex items-center gap-3 border-b border-border/60 px-4 py-4 sm:px-6"><button type="button" onClick={() => setBrowseOpen(false)} aria-label="Go back" className="grid size-9 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background"><svg viewBox="0 0 16 16" fill="currentColor" className="size-4"><path fillRule="evenodd" d="M6.78 4.22a.75.75 0 0 1 0 1.06L4.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L2.47 8.53a.75.75 0 0 1 0-1.06l3.25-3.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg></button><h3 className="flex-1 text-base font-semibold">Browse all degrees</h3></div><div className="border-b border-border/60 px-4 py-3 sm:px-6"><div className="relative"><svg viewBox="0 0 16 16" fill="currentColor" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"><path d="M6 2a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM0 6a6 6 0 1 1 10.89 3.477l4.817 4.816a.75.75 0 0 1-1.06 1.06l-4.816-4.816A6 6 0 0 1 0 6Z" /></svg><input type="text" placeholder={"Type your degree, e.g. Computer Science\u2026"} value={browseQuery} onChange={e_0 => setBrowseQuery(e_0.target.value)} autoFocus={true} className="w-full rounded-xl bg-surface py-2.5 pl-9 pr-9 text-sm ring-1 ring-border/60 focus:outline-none focus:ring-2 focus:ring-brand" />{browseQuery && <button type="button" onClick={() => setBrowseQuery("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"><svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg></button>}</div></div><div className="flex-1 overflow-y-auto">{browseFiltered.length === 0 ? <p className="px-6 py-12 text-center text-sm text-muted-foreground">No results for “{browseQuery}”</p> : <ul className="mx-auto max-w-3xl divide-y divide-border/60 p-4 sm:p-6">{browseFiltered.map(o_4 => {
            const active = o_4.value === value;
            return <li key={o_4.value}><button type="button" onClick={() => pick(o_4.value)} className={`flex w-full cursor-pointer items-start gap-3 rounded-xl px-3 py-4 text-left transition-colors hover:bg-surface ${active ? "bg-brand/5" : ""}`}><span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{o_4.label}</span><span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{o_4.degrees}</span></span><span className="shrink-0 rounded-full bg-surface px-2.5 py-1 text-[10px] font-medium text-muted-foreground">{o_4.software.length} tools</span>{active && <svg viewBox="0 0 16 16" fill="currentColor" className="mt-1 size-4 shrink-0 text-brand"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>}</button></li>;
          })}</ul>}</div></div>, document.body);
    $[36] = browseFiltered;
    $[37] = browseOpen;
    $[38] = browseQuery;
    $[39] = pick;
    $[40] = value;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  let t6;
  if ($[42] !== t1 || $[43] !== t2 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5) {
    t6 = <div className={t1}>{t2}{t3}{t4}{t5}</div>;
    $[42] = t1;
    $[43] = t2;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
  } else {
    t6 = $[47];
  }
  return t6;
}

/* ─────────────────────────────────────────────────────────────
   DegreeProfile — summary + software table for a selected degree
───────────────────────────────────────────────────────────── */
function _temp8(o, q) {
  const s = q.toLowerCase();
  return o.label.toLowerCase().includes(s) || o.degrees.toLowerCase().includes(s);
}
function DegreeProfile(t0) {
  const $ = _c(25);
  const {
    degree,
    onClear
  } = t0;
  let t1;
  if ($[0] !== degree.label) {
    t1 = <p className="text-sm font-semibold">{degree.label}</p>;
    $[0] = degree.label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== degree.software.length) {
    t2 = <p className="mt-0.5 text-xs text-muted-foreground">{degree.software.length} software tools pre-selected for you</p>;
    $[2] = degree.software.length;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = `Remove ${degree.label}`;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClear || $[9] !== t4) {
    t6 = <button type="button" onClick={onClear} aria-label={t4} className="grid size-7 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background">{t5}</button>;
    $[8] = onClear;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t6) {
    t7 = <div className="flex items-start justify-between gap-3 border-b border-border/60 px-4 py-4 sm:px-5">{t3}{t6}</div>;
    $[11] = t3;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== degree.summary) {
    t8 = <p className="px-4 py-4 text-sm leading-relaxed text-muted-foreground sm:px-5">{degree.summary}</p>;
    $[14] = degree.summary;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <thead><tr className="border-b border-border/60 text-[10px] uppercase tracking-widest text-muted-foreground"><th scope="col" className="px-4 py-2.5 font-semibold sm:px-5">Software</th><th scope="col" className="px-4 py-2.5 font-semibold">What you'll use it for</th></tr></thead>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== degree.software) {
    t10 = degree.software.map(_temp9);
    $[17] = degree.software;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <div className="overflow-x-auto border-t border-border/60"><table className="w-full min-w-[420px] text-left text-xs">{t9}<tbody className="divide-y divide-border/60">{t10}</tbody></table></div>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t7 || $[23] !== t8) {
    t12 = <div className="overflow-hidden rounded-2xl bg-card ring-1 ring-border/60">{t7}{t8}{t11}</div>;
    $[21] = t11;
    $[22] = t7;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}

/* ─────────────────────────────────────────────────────────────
   SquircleGrid
───────────────────────────────────────────────────────────── */
function _temp9(row) {
  return <tr key={row.name + row.usage} className="align-top"><td className="px-4 py-3 sm:px-5"><span className="block font-medium text-foreground">{row.name}</span><span className="mt-0.5 block text-[10px] text-muted-foreground">{row.category}</span></td><td className="px-4 py-3 text-muted-foreground">{row.usage}</td></tr>;
}
function SquircleGrid(t0) {
  const $ = _c(9);
  const {
    options,
    value,
    onChange
  } = t0;
  let t1;
  if ($[0] !== onChange || $[1] !== options || $[2] !== value) {
    let t2;
    if ($[4] !== onChange || $[5] !== value) {
      t2 = o => {
        const active = value === o.value;
        return <button key={o.value} onClick={() => onChange(o.value)} className={`flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded-[28px] p-3 text-center ring-1 transition-all ${active ? "bg-card text-foreground ring-2 ring-brand ring-offset-2 ring-offset-background shadow-lg" : "bg-card text-foreground ring-border/60 hover:ring-foreground/40 hover:shadow-md"}`}><div className="grid flex-1 place-items-center"><svg viewBox="0 0 24 24" fill="none" className={`size-8 ${active ? "text-brand" : "text-muted-foreground"}`}><rect x="3" y="5" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M2 19h20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg></div><div><p className="text-sm font-semibold">{o.label}</p>{o.sub && <p className="mt-0.5 text-[10px] text-muted-foreground">{o.sub}</p>}</div></button>;
      };
      $[4] = onChange;
      $[5] = value;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = options.map(t2);
    $[0] = onChange;
    $[1] = options;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/* ─────────────────────────────────────────────────────────────
   MultiSquircleGrid — multi-select variant of SquircleGrid
───────────────────────────────────────────────────────────── */

function MultiSquircleGrid(t0) {
  const $ = _c(22);
  const {
    options,
    value,
    onChange,
    blockedValues: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const blockedValues = t2;
  let t3;
  if ($[2] !== onChange || $[3] !== value) {
    t3 = v => {
      const next = value.includes(v) ? value.filter(x => x !== v) : [...value, v];
      onChange(next);
    };
    $[2] = onChange;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const toggle = t3;
  let t4;
  if ($[5] !== blockedValues || $[6] !== options || $[7] !== toggle || $[8] !== value) {
    let t5;
    if ($[10] !== blockedValues || $[11] !== toggle || $[12] !== value) {
      t5 = o => {
        const blocked = blockedValues.includes(o.value);
        const active = !blocked && value.includes(o.value);
        return <button key={o.value} onClick={() => toggle(o.value)} disabled={blocked} title={blocked ? "Ruled out by your software or game picks" : undefined} className={`relative flex aspect-square flex-col items-center justify-center gap-2 rounded-[28px] p-3 text-center ring-1 transition-all ${blocked ? "cursor-not-allowed bg-card text-muted-foreground opacity-45 ring-border/60" : active ? "cursor-pointer bg-card text-foreground ring-2 ring-brand ring-offset-2 ring-offset-background shadow-lg" : "cursor-pointer bg-card text-foreground ring-border/60 hover:ring-foreground/40 hover:shadow-md"}`}>{active && <span className="absolute right-2.5 top-2.5 grid size-5 place-items-center rounded-full bg-brand"><svg viewBox="0 0 16 16" fill="currentColor" className="size-3 text-brand-foreground"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg></span>}<div className="grid flex-1 place-items-center"><svg viewBox="0 0 24 24" fill="none" className={`size-8 ${active ? "text-brand" : "text-muted-foreground"}`}><rect x="3" y="5" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M2 19h20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg></div><div><p className={`text-sm font-semibold ${blocked ? "line-through" : ""}`}>{o.label}</p>{o.sub && <p className="mt-0.5 text-[10px] text-muted-foreground">{o.sub}</p>}</div></button>;
      };
      $[10] = blockedValues;
      $[11] = toggle;
      $[12] = value;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = options.map(t5);
    $[5] = blockedValues;
    $[6] = options;
    $[7] = toggle;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{t4}</div>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== blockedValues || $[17] !== value.length) {
    t6 = value.length === 0 && <p className="mt-3 text-xs text-muted-foreground">{blockedValues.length > 0 ? "Nothing selected, so we\u2019ll consider every brand except Apple." : "Nothing selected, so we\u2019ll consider all brands."}</p>;
    $[16] = blockedValues;
    $[17] = value.length;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

/* ─────────────────────────────────────────────────────────────
   MultiFeature
───────────────────────────────────────────────────────────── */

function MultiFeature({
  options,
  value,
  onChange
}: {
  options: {
    value: string;
    label: string;
    defaultChecked?: boolean;
  }[];
  value: string[] | undefined;
  onChange: (v: string[]) => void;
}) {
  useEffect(() => {
    if (value === undefined) {
      onChange(options.filter(o => o.defaultChecked).map(o => o.value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const current = value ?? options.filter(o => o.defaultChecked).map(o => o.value);
  const toggle = (v: string) => onChange(current.includes(v) ? current.filter(x => x !== v) : [...current, v]);
  return <ul className="divide-y divide-border/60 overflow-hidden rounded-2xl bg-card ring-1 ring-border/60">
      {options.map(o => {
      const active = current.includes(o.value);
      return <li key={o.value}>
            <button onClick={() => toggle(o.value)} className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left text-sm transition-colors hover:bg-surface">
              <span className="flex items-center gap-3">
                <span className={`grid size-5 place-items-center rounded-md ring-1 transition-colors ${active ? "bg-brand text-brand-foreground ring-brand" : "bg-background ring-border"}`}>
                  {active && <svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5">
                      <path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                    </svg>}
                </span>
                <span className="font-medium">{o.label}</span>
              </span>
              {o.defaultChecked && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Recommended
                </span>}
            </button>
          </li>;
    })}
    </ul>;
}